import { defineStore } from 'pinia';
import api from '@/api';

export const useComponentTypeStore = defineStore('componentType', {
  state: () => ({
    // List of all component types
    componentTypes: [],

    // Single component type
    componentType: null,

    // Lookups for dropdowns
    lookups: [],

    // Loading state
    loading: false,

    // Error state
    error: null
  }),

  getters: {
    getComponentTypes: (state) => state.componentTypes,
    getComponentType: (state) => state.componentType,
    getLookups: (state) => state.lookups,
    isLoading: (state) => state.loading,
    getError: (state) => state.error
  },

  actions: {
    /**
     * Fetch all component types
     * @param {Object} [params]
     * @param {boolean} [params.includeInactive]
     */
    async fetchComponentTypes(params = {}) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.componentType.getComponentTypes(params);
        this.componentTypes = resp.data || [];
      } catch (e) {
        this.error = e;
        this.componentTypes = [];
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch single component type by ID
     * @param {number} id
     */
    async fetchComponentType(id) {
      this.loading = true;
      this.error = null;
      // Clear immediately, not just on error - a detail view watching
      // route.params.id would otherwise briefly show the previous entity.
      this.componentType = null;
      try {
        const resp = await api.componentType.getComponentType(id);
        this.componentType = resp.data;
      } catch (e) {
        this.error = e;
        this.componentType = null;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch component type by code
     * @param {string} code
     */
    async fetchComponentTypeByCode(code) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.componentType.getComponentTypeByCode(code);
        this.componentType = resp.data;
      } catch (e) {
        this.error = e;
        this.componentType = null;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch component type lookups for dropdowns
     */
    async fetchLookups() {
      this.error = null;
      try {
        const resp = await api.componentType.getComponentTypeLookups();
        this.lookups = resp.data || [];
      } catch (e) {
        this.error = e;
        this.lookups = [];
        throw e;
      }
    },

    // Clear methods
    clearComponentTypes() {
      this.componentTypes = [];
    },

    clearComponentType() {
      this.componentType = null;
    },

    clearLookups() {
      this.lookups = [];
    },

    clearError() {
      this.error = null;
    },

    clearAll() {
      this.clearComponentTypes();
      this.clearComponentType();
      this.clearLookups();
      this.clearError();
    }
  }
});
